import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react';

import { NOTES_COLOR, ERROR_COLOR, SUCCESS_COLOR } from '../../theme';
import Field from '../fields/Field';
import Textareafield from '../fields/Textareafield';
import Btn from '../buttons/Btn';
import Cross from '../../images/icon-cross.inline.svg';
import UserStore from '../../stores/UserStore';
import AnnonceStore from '../../stores/AnnonceStore';
import { Suspended } from '../../hocs/withLoadingFallback';
import media from '../../utils/mediaQueries';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(53, 67, 84, 0.9);
  padding: 0 16px;
  //to overcome z-index: 10000 of the header
  z-index: 20000;
`;

const Content = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  box-shadow: 0 -1px 1px 0 rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(155, 132, 135, 0.06), 0 4px 12px 0 rgba(119, 94, 98, 0.05);
  border-radius: 12px;
  padding: 32px 40px 24px;
  width: 744px;
  max-width: calc(100% - 32px);
  max-height: 100%;
  overflow: ${(props) => props.overflow ? props.overflow : 'auto'};
  -webkit-overflow-scrolling: touch;

  ${media.mobile`
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    max-width: 100vw;
    padding: 20px 24px 24px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}
`;

const Note = styled.div`
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: -0.1px;
  color: ${NOTES_COLOR};
  margin-bottom: 16px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.2px;
  margin-bottom: 16px;
`;

const Subtitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.44;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
`;

const Description = styled.div`
  font-size: 18px;
  line-height: 1.33;
  letter-spacing: 0.2px;
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const SubColumn = styled.div`
  grid-row: 1 / 2;
  display: grid;
  grid-gap: 24px;

  ${media.mobile`
    grid-row: auto;
  `}
`;

const FullWidthRow = styled.div`
  grid-column: 1/-1;
`;

const CrossIcon = styled(Cross)`
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  
  path {
    fill: #354354;
  }
`;

const ResultMessage = styled.div`
  color: ${({ isSuccess }) => isSuccess ? SUCCESS_COLOR : ERROR_COLOR};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.17;
  margin-top: 8px;
`;

const validationSchema = Yup.object().shape({
  firstname: Yup.string().required('Ce champ est obligatoire.'),
  lastname: Yup.string().required('Ce champ est obligatoire.'),
  email: Yup.string()
    .email('Veuillez fournir une adresse électronique valide.')
    .required('Ce champ est obligatoire.'),
  phone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
      'Le téléphone doit être au format 01 02 03 04 05',
    )
    .required('Ce champ est obligatoire.'),
  message: Yup.string().required('Ce champ est obligatoire.'),
});

type ModalProps = {
  title: string;
  onClose(): void;
  annonceId: string;
};

const Modal = observer(({ title, onClose, annonceId }: ModalProps) => {
  const { userProInfo, isUserBusy } = UserStore;

  useEffect(() => {
    if (isEmpty(userProInfo)) {
      UserStore.getProUser();
    }
  }, [userProInfo]);

  const handleFormSubmit = useCallback((values, { setStatus, setErrors, setSubmitting }) => {
    const {
      firstname, lastname, email, phone, message,
    } = values;
    const valuesToBackendFormat = {
      first_name: firstname,
      last_name: lastname,
      email,
      phone: phone.replace(/ /g, ''),
      message,
    };
    AnnonceStore.postAnswer({
      id: annonceId,
      answer: valuesToBackendFormat,
      setErrors,
      setStatus,
      setSubmitting,
    });
  }, []);

  const initialValues = {
    firstname: userProInfo?.firstname || '',
    lastname: userProInfo?.lastname || '',
    email: userProInfo?.email || '',
    phone: userProInfo?.phone || '',
    message: '',
  };

  return (
    <Container>
      <Content>
        <CrossIcon onClick={onClose} />
        <Note>Vous répondez à l’annonce</Note>
        <Title>{title}</Title>
        <Subtitle>Vos coordonnées</Subtitle>
        <Description>Elles seront transmises à l’auteur de l’annonce afin qu’il prennent contact avec vous.</Description>
        <Suspended isLoading={isUserBusy}>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              status,
            }) => (
              <Form onSubmit={handleSubmit}>
                <SubColumn>
                  <Field
                    type="text"
                    label="Prénom"
                    placeholder="ex: Jean-Paul"
                    name="firstname"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstname}
                    valid={touched.firstname && !errors.firstname}
                    errorMessage={errors.firstname && touched.firstname ? errors.firstname : null}
                  />
                  <Field
                    type="text"
                    label="Nom"
                    placeholder="ex: Belmondo"
                    name="lastname"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.lastname}
                    valid={touched.lastname && !errors.lastname}
                    errorMessage={errors.lastname && touched.lastname ? errors.lastname : null}
                  />
                </SubColumn>
                <SubColumn>
                  <Field
                    type="email"
                    label="Adresse email"
                    placeholder="ex: albert@vroomvroom.fr"
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    valid={touched.email && !errors.email}
                    errorMessage={errors.email && touched.email ? errors.email : null}
                  />
                  <Field
                    type="text"
                    label="Tél. / portable"
                    placeholder="ex: 06 01 02 03 04"
                    name="phone"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                    valid={touched.phone && !errors.phone}
                    errorMessage={errors.phone && touched.phone ? errors.phone : null}
                  />
                </SubColumn>
                <FullWidthRow>
                  <Textareafield
                    label="Votre message"
                    name="message"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.message}
                    valid={touched.message && !errors.message}
                    errorMessage={errors.message && touched.message ? errors.message : null}
                    placeholder="Demandez des précisions sur l'annonce, indiquez vos disponibilités pour un rendez-vous téléphonique ou physique, etc."
                  />
                </FullWidthRow>
                <FullWidthRow>
                  <Btn type="submit" text="Envoyer" disabled={isSubmitting} />
                  {status && status.success && <ResultMessage isSuccess>Merci ! Vous allez être recontacté(e) sous peu</ResultMessage>}
                  {status && !status.success && <ResultMessage>Une erreur est survenue, veuillez réessayer</ResultMessage>}
                </FullWidthRow>
              </Form>
            )}
          </Formik>
        </Suspended>
      </Content>
    </Container>
  );
});

export { Modal };
